// File: typescript/apps/front-end/src/main/react/thoughts/ThoughtAncestorBreadcrumb.tsx
//
// Focused-comment context. When the discussion page is opened on a nested reply's permalink
// (/discussion/{slug}/thought/{id}, e.g. via "Continue this thread" or a "X responded to your
// thought" notification), the reply renders as the visual root and would otherwise lose all upward
// context. This renders that context above it:
//
//   ← Back to post
//   ↰ In reply to  (av) Ovid › (av) Phoenix        ← avatar breadcrumb: the full ancestor path
//   [ direct parent, full body, dimmed, read-only ] ← additive; the last crumb's content
//
// The DIRECT PARENT gets its body rendered, not just an avatar, so a reader arriving from a reply
// notification can see what they themselves said without navigating away from the reply (owner
// report 2026-07-22). Everything further up stays a compact one-line breadcrumb.
//
// Data: GET /ajax/thoughts/ancestors (ClientFactory.listThoughtAncestors), root-first, focused
// argument excluded. The endpoint already returns the FULL node (htmlContent, spoiler, lifecycle
// fields) for every ancestor, so the parent card needs no extra request. Each crumb links to that
// ancestor's own focused permalink.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, ChevronDown, ChevronRight, ChevronUp, CornerUpLeft } from "lucide-react";
import ProfileImage from "@/main/react/buttons/ProfileImage";
import ThoughtDisplayerInDiscussion from "@/main/react/thoughts/ThoughtDisplayerInDiscussion";
import { ClientFactory } from "@/main/common/ClientFactory";
import { getDisplayUsername, hasDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import type { ThreadNode } from "@/main/react/thoughts/ThoughtThread";
import { Localizer } from "@/main/react/Types";
import { PaginatedListRetryState } from "@/main/react/list/PaginatedListState";
import logger from "@/lib/logger";
import { thoughtPermalinkHref } from "@/main/react/thoughts/thoughtChainRoute";

// Collapsed height of the parent context card's body, in px. Roughly six lines of body copy — enough
// to recognise your own comment, short enough that the reply the notification sent you to stays in
// view. Only enforced when the body actually exceeds it (measured, see ParentContextCard).
const PARENT_CONTEXT_COLLAPSED_MAX_HEIGHT_PX = 148;

function focusedArgumentHref(discussionSlug: string, argumentPublicId: string): string | null {
    // Guarded builder, not a local template literal — see thoughtPermalinkHref for why a missing id
    // yields a clickable `/thought/null` rather than an obvious break.
    return thoughtPermalinkHref(discussionSlug, argumentPublicId);
}

function discussionHref(discussionSlug: string): string {
    // Target the canonical "conversations" section route, not the bare /discussion/{slug}. The
    // breadcrumb renders inside an already-mounted DiscussionPage (focused-argument view), so this is
    // an in-page soft navigation: the page's internal router renders the full list at the section
    // route but treats the bare route as a no-match (blank). The bare path only renders on a fresh
    // hard load, which normalizes it to /conversations anyway.
    return `/discussion/${encodeURIComponent(discussionSlug)}/conversations`;
}

export default function ThoughtAncestorBreadcrumb(props: {
    argumentPublicId: string;
    discussionSlug: string;
    localizer: Localizer;
    loggedInUsername?: string;
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
}) {
    const {
        argumentPublicId,
        discussionSlug,
        localizer,
        loggedInUsername,
        isSensitive = false,
        suppressSpoilerGuards = false,
    } = props;
    const [ancestors, setAncestors] = useState<ThreadNode[] | null>(null);
    // F-F2 (2026-08-27): failure is its own state (rule 23's state contract). The old catch wrote
    // `[]`, so a reader arriving from a reply notification silently lost the whole upward context
    // whenever the fetch failed. Retry is a nonce bump that re-runs the effect.
    const [loadFailed, setLoadFailed] = useState(false);
    const [retryNonce, setRetryNonce] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoadFailed(false);
        if (!argumentPublicId) {
            setAncestors([]);
            return;
        }
        setAncestors(null);
        ClientFactory.listThoughtAncestors<ThreadNode[]>(argumentPublicId)
            .then((resp) => {
                if (!cancelled) {
                    setAncestors(Array.isArray(resp.data) ? resp.data : []);
                }
            })
            .catch((err) => {
                logger.error("[ThoughtAncestorBreadcrumb] failed to load ancestors", err);
                if (!cancelled) {
                    setLoadFailed(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [argumentPublicId, retryNonce]);

    const backToDiscussion = (
        <a
            href={discussionHref(discussionSlug)}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary no-underline hover:underline"
            data-playwright-id="thought-back-to-discussion-link"
        >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {localizer("thread.back_to_discussion")}
        </a>
    );

    // The breadcrumb keeps listing the FULL chain including the direct parent, and the parent card is
    // purely additive below it. Promoting the parent out of the breadcrumb was considered and
    // rejected: the crumb is the only affordance for climbing to the parent's own focused view (the
    // card's avatar/name link to the author's profile, per the shared row renderer), so removing it
    // would have cost navigation to save a little redundancy. The two also read as different things —
    // the breadcrumb is the path, the card is the content.
    // Until the chain resolves we still show "Back to discussion" so the view is never a dead end.
    const crumbs = ancestors ?? [];
    const parent = crumbs.length > 0 ? crumbs[crumbs.length - 1] : null;

    return (
        <div className="mb-3 flex min-w-0 max-w-full flex-col gap-2 px-3" data-playwright-id="thought-ancestor-context">
            {backToDiscussion}
            {loadFailed ? (
                <PaginatedListRetryState
                    message={localizer("shared.action.failed")}
                    retryLabel={localizer("literal.retry")}
                    onRetry={async () => setRetryNonce((nonce) => nonce + 1)}
                />
            ) : null}
            {crumbs.length > 0 ? (
                <nav
                    aria-label={localizer("thread.in_reply_to")}
                    className="flex min-w-0 max-w-full flex-wrap items-center gap-x-1 gap-y-1 text-xs text-muted-foreground"
                >
                    <CornerUpLeft className="size-3.5 shrink-0" aria-hidden="true" />
                    <span className="shrink-0">{localizer("thread.in_reply_to")}</span>
                    {crumbs.map((ancestor, index) => {
                        const publicId = ancestor.publicId ?? "";
                        const isLast = index === (crumbs.length - 1);
                        return (
                            <React.Fragment key={publicId || index}>
                                <a
                                    href={focusedArgumentHref(discussionSlug, publicId) ?? discussionHref(discussionSlug)}
                                    className="standard-hover inline-flex min-w-0 items-center gap-1 rounded-full px-1 py-0.5 no-underline hover:text-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                    data-playwright-id="thought-ancestor-crumb"
                                    data-thought-public-id={publicId}
                                >
                                    <ProfileImage localizer={localizer}
                                        imageUrl={ancestor.profileImageUrl}
                                        username={ancestor.username}
                                        mode={"user"}
                                        interactive={false}
                                        className={"size-5 shrink-0"}
                                    />
                                    <span className="truncate font-medium" translate={hasDisplayUsername(ancestor.username) ? "no" : undefined}>{getDisplayUsername(ancestor.username, localizer)}</span>
                                </a>
                                {!isLast ? <ChevronRight className="size-3 shrink-0" aria-hidden="true" /> : null}
                            </React.Fragment>
                        );
                    })}
                </nav>
            ) : null}
            {parent ? (
                <ParentContextCard
                    parent={parent}
                    discussionSlug={discussionSlug}
                    localizer={localizer}
                    loggedInUsername={loggedInUsername}
                    isSensitive={isSensitive}
                    suppressSpoilerGuards={suppressSpoilerGuards}
                />
            ) : null}
        </div>
    );
}

// The direct parent, rendered read-only as upward context for the focused reply.
//
// Body rendering is delegated to ThoughtDisplayerInDiscussion (the same renderer the tree, list and
// chain views use) rather than hand-rolled markup, so the spoiler guard, sensitive-media mask and the
// deleted-thought tombstone all apply here for free — a bespoke card would have leaked all three.
// `hideActionButtons` drops the whole action row: this card is context, and a second vote/share/reply
// row stacked directly above the focused thought's own would read as two competing sets of controls.
function ParentContextCard(props: {
    parent: ThreadNode;
    discussionSlug: string;
    localizer: Localizer;
    loggedInUsername?: string;
    isSensitive: boolean;
    suppressSpoilerGuards: boolean;
}) {
    const { parent, discussionSlug, localizer, loggedInUsername, isSensitive, suppressSpoilerGuards } = props;
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const publicId = parent.publicId ?? "";

    // Measure rather than guess whether the clamp is needed, so a short parent never renders a
    // pointless "Show more". Measured in a layout effect (pre-paint) to avoid a visible flash of the
    // toggle appearing.
    //
    // Compare scrollHeight against the THRESHOLD, not against clientHeight: when the body is not
    // currently clamped those two are equal by definition, so a clientHeight comparison could never
    // fire and the toggle would never appear. scrollHeight is the full content height in both states
    // (overflow:hidden does not change it), which makes this stable across expand/collapse.
    //
    // A ResizeObserver re-runs it because rich content settles late — an image or embed in the parent
    // finishes loading after mount and changes the height, which a one-shot measurement would miss.
    useLayoutEffect(() => {
        setIsExpanded(false);
        const el = bodyRef.current;
        if (!el) {
            setIsOverflowing(false);
            return;
        }
        const measure = () => setIsOverflowing(el.scrollHeight > PARENT_CONTEXT_COLLAPSED_MAX_HEIGHT_PX);
        measure();
        if (typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(measure);
        // Observe the CONTENT box, not the clamped wrapper: once the clamp is on, the wrapper is
        // pinned to maxHeight and stops reporting size changes, so observing it would go deaf exactly
        // when content is still settling. The child's height is never constrained.
        observer.observe(el.firstElementChild ?? el);
        return () => observer.disconnect();
    }, [publicId, parent.htmlContent]);

    const toggleExpanded = useCallback(() => setIsExpanded((v) => !v), []);

    const isClamped = isOverflowing && !isExpanded;

    return (
        <div
            className="rounded-lg border border-border bg-card px-2 py-1 opacity-80"
            data-playwright-id="thought-parent-context-card"
            data-thought-public-id={publicId}
        >
            <div
                ref={bodyRef}
                className={isClamped ? "relative overflow-hidden" : undefined}
                style={isClamped ? { maxHeight: `${PARENT_CONTEXT_COLLAPSED_MAX_HEIGHT_PX}px` } : undefined}
            >
                <ThoughtDisplayerInDiscussion
                    argument={parent}
                    conversationId={null}
                    discussionSlug={discussionSlug}
                    isAuthorTheLoggedInUser={Boolean(loggedInUsername) && parent.username === loggedInUsername}
                    localizer={localizer}
                    isSensitive={isSensitive}
                    suppressSpoilerGuard={suppressSpoilerGuards}
                    contentWrapperClassName="pl-6"
                    hideActionButtons={true}
                />
                {isClamped ? (
                    // Fade the clamped edge so the cut reads as intentional rather than as a render
                    // bug. Anchored INSIDE the clamped body (not the card) so it tracks the actual cut
                    // line rather than depending on the toggle button's height, and fades to `card` —
                    // the surface it actually sits on. Must not swallow clicks aimed at links in the
                    // body, hence pointer-events-none.
                    <div
                        className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-card"
                        aria-hidden="true"
                    />
                ) : null}
            </div>
            {isOverflowing ? (
                <button
                    type="button"
                    onClick={toggleExpanded}
                    aria-expanded={isExpanded}
                    className="standard-hover mt-0.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                    data-playwright-id="thought-parent-context-toggle"
                >
                    {isExpanded ? (
                        <ChevronUp className="size-3.5" aria-hidden="true" />
                    ) : (
                        <ChevronDown className="size-3.5" aria-hidden="true" />
                    )}
                    {localizer(isExpanded ? "thread.parent_context.show_less" : "thread.parent_context.show_more")}
                </button>
            ) : null}
        </div>
    );
}
